/**
 * Real-time street/zip/city autocomplete for the customer address form (CTVSP-8676).
 *
 * Keys all field lookups on the `name` attribute because the id attribute is not consistent
 * across address-form templates. A legacy form prefixes its controls (`<formName>[strasse]`,
 * `<formName>[plz]`, …); a form that names them plainly (`strasse`, `plz`, …) opts in with
 * `js_address_autocomplete` on its street input.
 *
 * Idempotent on a per-input basis — safe to call from multiple bundles or repeatedly
 * after dynamic DOM updates.
 *
 * The backing DHL DataFactory ships German addresses only. The country select is read
 * live per fetch so a customer who picks France gets an empty dropdown rather than
 * misleading German matches.
 */

import { createAutoComplete } from './autocomplete.js';
import { escapeHtml } from './utils/escape-html.js';

const SERVICE_URL = '/addressAutocompleteEndpoint.php';
const TRACKING_URL = '/addressAutocompleteTrackingEndpoint.php';
const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const STREET_INPUTS = 'input[name$="[strasse]"], input.js_address_autocomplete';

const attachedInputs = new WeakSet();

/**
 * @typedef {{street: HTMLInputElement, zip: HTMLInputElement|null, city: HTMLInputElement|null, country: HTMLSelectElement|null}} AddressFields
 */

export function attachAddressAutocomplete() {
  document.querySelectorAll(STREET_INPUTS).forEach((input) => {
    if (attachedInputs.has(input)) {
      return;
    }
    attachedInputs.add(input);
    const fields = siblingFields(input);
    createAutoComplete(
      input,
      () => buildServiceUrl(fields),
      (suggestion, inputEl) => fillAddressFields(suggestion, fields, inputEl.value),
      (suggestion, query, response) => renderSuggestion(suggestion, query, response),
      {
        minChars: MIN_CHARS,
        debounce: DEBOUNCE_MS,
        ariaLabel: 'Adressvorschläge',
        containerClass: 'address_autocomplete',
        noResultsMessage: 'Keine passende Adresse gefunden',
      },
    );
  });
}

/**
 * The other controls share the street's naming: `adresse[strasse]` has `adresse[plz]`, `strasse` has `plz`.
 *
 * @param {HTMLInputElement} streetInput
 * @returns {AddressFields}
 */
function siblingFields(streetInput) {
  const scope = streetInput.form ?? document;
  const named = (part) => scope.querySelector(`[name="${streetInput.name.replace(/strasse(\]?)$/, `${part}$1`)}"]`);

  return { street: streetInput, zip: named('plz'), city: named('ort'), country: named('land_id') };
}

/** @param {AddressFields} fields */
function buildServiceUrl(fields) {
  const params = new URLSearchParams({ country: valueOf(fields.country) });
  const zip = valueOf(fields.zip);
  const city = valueOf(fields.city);
  if (zip) {
    params.set('zip', zip);
  }
  if (city) {
    params.set('city', city);
  }
  return `${SERVICE_URL}?${params.toString()}`;
}

/** @param {HTMLInputElement|HTMLSelectElement|null} field */
function valueOf(field) {
  return field ? field.value.trim() : '';
}

/** @param {AddressFields} fields */
function fillAddressFields(suggestion, fields, originalInput) {
  const houseNumber = extractHouseNumber(originalInput, suggestion.street);
  const streetValue = houseNumber ? `${suggestion.street} ${houseNumber}` : `${suggestion.street} `;
  setFieldValue(fields.street, streetValue);
  setFieldValue(fields.zip, suggestion.zip);
  setFieldValue(fields.city, suggestion.city);

  fields.street.focus();
  fields.street.setSelectionRange(fields.street.value.length, fields.street.value.length);

  trackSelection(fields, originalInput);
}

/** @param {AddressFields} fields */
function trackSelection(fields, originalInput) {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return;
  }
  const params = new URLSearchParams({
    country: valueOf(fields.country),
    queryLength: String(originalInput.length),
  });
  navigator.sendBeacon(`${TRACKING_URL}?${params.toString()}`);
}

/**
 * Pull a house-number-style token out of the user's typed input so it survives selection.
 * Matches `12`, `12a`, `12-14`, `12/4`, etc. Five-digit numbers are excluded (those are ZIPs).
 * Returns '' if the input is not strictly longer than the canonical street, which guards
 * against streets like "Straße 1" or "An der B 187a" whose own names end in digits.
 *
 * Mirrors AddressQueryParser's letter-or-dot→digit preprocessing so "Goethestr.12" survives
 * selection the same way "Goethestr. 12" does on the server side.
 */
function extractHouseNumber(userInput, suggestionStreet) {
  const userTrimmed = userInput.trim();
  if (userTrimmed.length <= suggestionStreet.trim().length) {
    return '';
  }
  const preprocessed = userTrimmed.replace(/(?<=[\p{L}.])(?=\d)/gu, ' ');
  const match = preprocessed.match(/\s(\d{1,4}\p{L}?(?:[-/]\d+\p{L}?)?)(?=\s|,|$)/u);
  return match ? match[1] : '';
}

/** @param {HTMLInputElement|null} el */
function setFieldValue(el, value) {
  if (!el) {
    return;
  }
  el.value = value;
  // Re-trigger existing blur-based validators (e.g. checkField in ankauf-address.js).
  el.dispatchEvent(new Event('change', { bubbles: true }));
  el.dispatchEvent(new Event('blur', { bubbles: true }));
}

/**
 * Bolds each matched part of the suggestion in its own column, using the parsed query
 * the backend returns alongside the suggestions. Each column highlights independently,
 * so a multi-part query like "Siemensstr. 48143 Münster" bolds the street, ZIP and city
 * each in turn — the previous single-substring match could not span columns.
 */
function renderSuggestion(suggestion, query, response) {
  const parsed = (response && response.parsed) || {};
  const houseNumber = parsed.houseNumber || extractHouseNumber(query, suggestion.street);

  const streetBody = boldPrefix(suggestion.street, parsed.streetPart || '');
  const streetHtml = houseNumber ? `${streetBody} <strong>${escapeHtml(houseNumber)}</strong>` : streetBody;
  const zipHtml = boldPrefix(suggestion.zip, parsed.zipPrefix || '');
  const cityHtml = boldPrefix(suggestion.city, parsed.cityPrefix || '');

  return `${streetHtml}, ${zipHtml} ${cityHtml}`;
}

function boldPrefix(value, prefix) {
  const escaped = escapeHtml(value);
  if (!prefix) {
    return escaped;
  }
  const haystack = value.toLowerCase();
  const needle = prefix.toLowerCase();
  if (!haystack.startsWith(needle)) {
    return escaped;
  }
  return `<strong>${escapeHtml(value.slice(0, prefix.length))}</strong>${escapeHtml(value.slice(prefix.length))}`;
}
